import React, { Suspense, useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { ChevronDown, Trash2 } from 'lucide-react';

import { bff } from '@/lib/http/bff';
import {
  fetchWorkflowScopesApi,
  fetchScopeNamespacesApi,
  type WorkflowScope,
  type ScopeNamespaceCount,
} from '@/features/system/orchestrator/api/scopesApi';
import {
  fetchOutcomeStepEventsApi,
  type ListStepOutcomeEventsResponse,
  type StepOutcomeEvent,
} from '@/features/system/orchestrator/api/outcomeEventsApi';
import { type WorkflowCatalogEntry } from '@/features/system/orchestrator/api/workflowsApi';
import { fetchWorkflowsV2Api } from '@/features/system/orchestrator/api/workflowsV2Api';
import {
  OutcomeTable,
  type WorkflowRunOutcomeRow,
} from '../outcomes/WorkflowRunOutcomesPanel';
import OutcomeTrackLineAudioPlayer from '../outcomes/OutcomeTrackLineAudioPlayer';
import WorkflowRunnerForm from '../runner/WorkflowRunnerForm';
import WorkflowRunGraphLive from '../runner/WorkflowRunGraphLive';
import DarkDataTable from '@/shared/components/ui/DarkDataTable';
import { timeAgo } from '@/utils/date';
import { lookupScopeRenderer } from './scopeRendererRegistry';
import NamespaceList, { SearchInput } from '@/shared/components/ui/NamespaceList';
import ScopeDeleteModal from './ScopeDeleteModal';

const DeviceInstanceInline = React.lazy(
  () => import('./renderers/DeviceInstanceInline'),
);

const SCOPE_KEY_LIMIT = 500;
const NS_QUERY_KEY = ['developer', 'scopeNamespaces'] as const;

/** Format milliseconds into human-readable duration (e.g. 1234 → "1s", 65432 → "1m 5s"). */
function fmtDurationMs(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const total = Math.floor(ms);
  const s = Math.floor(total / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m ${s % 60}s`;
  if (m > 0) return `${m}m ${s % 60}s`;
  if (s > 0) return `${s}.${String(total % 1000).padStart(3, '0').slice(0, 1)}s`;
  return `${total}ms`;
}

/** Normalise the two field-name variants the API may return. */
function ns(s: WorkflowScope): string {
  return s.scope_namespace ?? s.scopeNamespace ?? '';
}
function sk(s: WorkflowScope): string {
  return s.scope_key ?? s.scopeKey ?? '';
}
function idScope(s: WorkflowScope): number | undefined {
  return s.id_scope;
}

/** Decode a scope key like `v1:eyJ2Ijox...` → human-readable field values.
 *  Format: `v1:<base64({"v":1,"fields":[["field_name","value"],...]})>`
 *  Also handles legacy flat format: `v1:<base64({"field_name":"value",...})>` */
function decodeScopeKey(raw: string): { label: string; fields: [string, string][] } | null {
  const m = raw.match(/^v\d+:(.+)$/);
  if (!m) return null;
  try {
    const json = atob(m[1]);
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed?.fields)) {
      const fields: [string, string][] = parsed.fields;
      const label = fields.map(([, v]) => v).join(' · ');
      return { label, fields };
    }
    // Legacy flat-object format: {"project_name":"cono hielo"}
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const entries = Object.entries(parsed).filter(
        ([k]) => k !== 'v' && k !== 'version',
      );
      if (entries.length > 0) {
        const fields: [string, string][] = entries.map(([k, v]) => [k, String(v)]);
        const label = fields.map(([, v]) => v).join(' · ');
        return { label, fields };
      }
    }
    return null;
  } catch {
    return null;
  }
}

/** Sanitise a value into a slug: lowercase, spaces→underscores, strip non-alnum. */
function sanitizeName(v: string): string {
  return v
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

/** Format an ISO date as YYMMDD prefix, e.g. "2026-02-09T…" → "260209". */
function datePrefix(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const yy = String(d.getFullYear()).slice(2);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}${mm}${dd}`;
}

type SortKey = 'scopeKey' | 'created' | 'updated';
type SortDir = 'asc' | 'desc';

function sortIndicator(active: boolean, dir: SortDir): string {
  if (!active) return '';
  return dir === 'asc' ? ' ▲' : ' ▼';
}

function SortHeader({
  label,
  active,
  dir,
  onClick,
  title,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      className={
        'inline-flex items-center gap-1 whitespace-nowrap cursor-default ' +
        (active ? 'text-neutral-300' : 'text-neutral-500 hover:text-neutral-300')
      }
      title={title}
    >
      <span>{label}</span>
      <span className="text-[10px]">{sortIndicator(active, dir)}</span>
    </button>
  );
}

/* SearchInput now provided by shared NamespaceList */

/* ── Inline outcomes panel (rendered inside expanded row) ──────── */
/**
 * Multi-run aware: fetches step events for the scope, discovers distinct
 * run_ids, then loads outcomes for each run.
 */
function ScopeOutcomesInline({ scope, scopeNamespace }: { scope: WorkflowScope; scopeNamespace: string }) {
  const id = idScope(scope);

  function isAudioGsUri(v: unknown): v is string {
    if (typeof v !== 'string') return false;
    const s = v.trim().toLowerCase();
    return s.startsWith('gs://') && (
      s.endsWith('.opus') || s.endsWith('.ogg') || s.endsWith('.mp3') ||
      s.endsWith('.wav') || s.endsWith('.m4a') || s.endsWith('.flac') || s.endsWith('.webm')
    );
  }

  function toGsUriFromGoogleStorageHttp(v: unknown): string {
    if (typeof v !== 'string') return '';
    const s = v.trim();
    const m = s.match(/^https?:\/\/storage\.googleapis\.com\/([^/?#]+)\/([^?#]+)$/i);
    if (!m) return '';
    const bucket = m[1];
    const objectPath = m[2];
    if (!bucket || !objectPath) return '';
    let decodedPath = objectPath;
    try { decodedPath = decodeURIComponent(objectPath); } catch {}
    const gs = `gs://${bucket}/${decodedPath}`;
    return isAudioGsUri(gs) ? gs : '';
  }

  function releaseAudioUri(stepRows: WorkflowRunOutcomeRow[]): string {
    const releaseRows = stepRows.filter((r) => (r.StepKey ?? '').trim() === 'release');

    const fromRows = (keys: string[]): string => {
      for (const key of keys) {
        for (const row of releaseRows) {
          if ((row.OutcomeKey ?? '').trim() !== key) continue;
          const raw = row.Value;
          if (isAudioGsUri(raw)) return String(raw).trim();
          const fromHttp = toGsUriFromGoogleStorageHttp(raw);
          if (fromHttp) return fromHttp;
        }
      }
      return '';
    };

    const direct = fromRows(['outputs.gcs_uri', 'outputs.http_url']);
    if (direct) return direct;

    for (const row of releaseRows) {
      const key = (row.OutcomeKey ?? '').trim();
      if (key !== 'outputs.result' && key !== 'result') continue;
      const raw = row.Value;
      const parsed = (() => {
        if (!raw) return null;
        if (typeof raw === 'object') return raw as any;
        if (typeof raw === 'string') {
          try { return JSON.parse(raw); } catch { return null; }
        }
        return null;
      })();
      if (parsed && typeof parsed === 'object') {
        const fromGs = isAudioGsUri((parsed as any).gcs_uri) ? String((parsed as any).gcs_uri).trim() : '';
        if (fromGs) return fromGs;
        const fromHttp = toGsUriFromGoogleStorageHttp((parsed as any).http_url);
        if (fromHttp) return fromHttp;
      }
    }
    return '';
  }

  function releaseVersion(stepRows: WorkflowRunOutcomeRow[]): string {
    const releaseRows = stepRows.filter((r) => (r.StepKey ?? '').trim() === 'release');
    const keys = ['outputs.version', 'outputs.release_version', 'outputs.semver', 'outputs.tag', 'version'];
    for (const key of keys) {
      for (const row of releaseRows) {
        if ((row.OutcomeKey ?? '').trim() !== key) continue;
        const v = row.Value;
        if (typeof v === 'number' && Number.isFinite(v)) return String(v);
        if (typeof v === 'string' && v.trim()) return v.trim();
      }
    }
    for (const row of releaseRows) {
      const key = (row.OutcomeKey ?? '').trim();
      if (key !== 'outputs.result' && key !== 'result') continue;
      const raw = row.Value;
      const parsed = (() => {
        if (!raw) return null;
        if (typeof raw === 'object') return raw as any;
        if (typeof raw === 'string') {
          try { return JSON.parse(raw); } catch { return null; }
        }
        return null;
      })();
      if (parsed && typeof parsed === 'object') {
        const nested = (parsed as any).version ?? (parsed as any).release_version ?? (parsed as any).semver ?? (parsed as any).tag;
        if (typeof nested === 'number' && Number.isFinite(nested)) return String(nested);
        if (typeof nested === 'string' && nested.trim()) return nested.trim();
      }
    }
    return '—';
  }

  function isReleaseRun(stepRows: WorkflowRunOutcomeRow[]): boolean {
    const keys = Array.from(new Set(stepRows.map((r) => (r.StepKey ?? '').trim()).filter(Boolean)));
    return keys.length === 1 && keys[0] === 'release';
  }

  /* Step 1 — discover all run_ids + their timestamps for this scope. */
  const runsQuery = useQuery({
    queryKey: ['developer', 'workflowScopes', 'scopeRuns', id] as const,
    queryFn: async () => {
      if (id == null) return { runIds: [] as number[], timestamps: {} as Record<number, string> };

      const allItems: StepOutcomeEvent[] = [];
      let cursorCreatedAt: string | undefined = undefined;
      let cursorOutcomeId: number | undefined = undefined;
      const nowIso = new Date().toISOString();

      // Page through historical outcomes to discover every run for this scope.
      // Safety guard avoids infinite loops if backend repeats cursors.
      for (let page = 0; page < 50; page++) {
        const res: ListStepOutcomeEventsResponse = await fetchOutcomeStepEventsApi({
          id_scope: id,
          include_history: true,
          from: '1970-01-01T00:00:00Z',
          to: nowIso,
          limit: 500,
          cursor_created_at: cursorCreatedAt,
          cursor_outcome_id: cursorOutcomeId,
        });

        const items: StepOutcomeEvent[] = Array.isArray(res.items) ? res.items : [];
        if (items.length === 0) break;
        allItems.push(...items);

        const next: ListStepOutcomeEventsResponse['next_cursor'] = res.next_cursor ?? null;
        const nextCreatedAt: string | undefined = typeof next?.created_at === 'string' ? next.created_at : undefined;
        const nextOutcomeId: number | undefined = Number.isFinite(Number(next?.outcome_id)) ? Number(next!.outcome_id) : undefined;
        if (!nextCreatedAt || nextOutcomeId == null) break;

        // Loop guard: stop if cursor does not advance.
        if (nextCreatedAt === cursorCreatedAt && nextOutcomeId === cursorOutcomeId) break;
        cursorCreatedAt = nextCreatedAt;
        cursorOutcomeId = nextOutcomeId;
      }

      // Extract unique run_ids preserving first-seen order (most recent first).
      // Also capture the most recent created_at per run (events arrive newest-first).
      const seen = new Set<number>();
      const runIds: number[] = [];
      const timestamps: Record<number, string> = {};
      for (const item of allItems) {
        const rid = item.run_id;
        if (rid != null && !seen.has(rid)) {
          seen.add(rid);
          runIds.push(rid);
          timestamps[rid] = item.created_at; // first seen = most recent event
        }
      }
      return { runIds, timestamps };
    },
    enabled: id != null,
    staleTime: 30_000,
  });

  const runIds = runsQuery.data?.runIds ?? [];
  const runTimestamps = runsQuery.data?.timestamps ?? {};

  const runOutcomesQueries = useQueries({
    queries: runIds.map((rid) => ({
      queryKey: ['developer', 'workflowScopes', 'runOutcomes', rid] as const,
      queryFn: async () => {
        return await bff.get<{
          run_outcomes?: WorkflowRunOutcomeRow[];
          step_outcomes?: WorkflowRunOutcomeRow[];
        }>(`/orchestrator/runs/${encodeURIComponent(String(rid))}/outcomes`);
      },
      staleTime: 10_000,
      enabled: runIds.length > 0,
    })),
  });

  const releaseRunRows = useMemo(() => {
    const rows: Array<{ runId: number; version: string; audioUri: string; createdAt?: string }> = [];
    for (let i = 0; i < runIds.length; i++) {
      const rid = runIds[i];
      const q = runOutcomesQueries[i];
      const stepRows: WorkflowRunOutcomeRow[] = ((q?.data as any)?.step_outcomes ?? []) as WorkflowRunOutcomeRow[];
      if (!stepRows.length || !isReleaseRun(stepRows)) continue;
      const audioUri = releaseAudioUri(stepRows);
      if (!audioUri) continue;
      rows.push({
        runId: rid,
        version: releaseVersion(stepRows).replace(/^v/i, ''),
        audioUri,
        createdAt: runTimestamps[rid],
      });
    }
    return rows;
  }, [runIds, runOutcomesQueries, runTimestamps]);

  const nonReleaseRunIds = useMemo(() => {
    const ids: number[] = [];
    for (let i = 0; i < runIds.length; i++) {
      const rid = runIds[i];
      const q = runOutcomesQueries[i];
      const stepRows: WorkflowRunOutcomeRow[] = ((q?.data as any)?.step_outcomes ?? []) as WorkflowRunOutcomeRow[];
      // While loading, keep run visible in normal renderer to avoid disappearing rows.
      if (q?.isLoading) {
        ids.push(rid);
        continue;
      }
      if (!stepRows.length || !isReleaseRun(stepRows)) ids.push(rid);
    }
    return ids;
  }, [runIds, runOutcomesQueries]);

  if (runsQuery.isLoading) return <div className="px-3 py-3 text-xs text-neutral-400">Loading outcomes…</div>;
  if (runsQuery.error) return <div className="px-3 py-3 text-xs text-rose-400">Failed: {(runsQuery.error as any)?.message ?? String(runsQuery.error)}</div>;
  if (runIds.length === 0) return <div className="px-3 py-3 text-xs text-neutral-500">No runs found for id_scope={id}.</div>;

  return (
    <div className="px-3 py-3 space-y-4">
      <div className="text-[10px] text-neutral-500">
        {runIds.length} run{runIds.length !== 1 ? 's' : ''} for id_scope={id}
      </div>

      {releaseRunRows.length > 0 && (
        <div className="rounded-md border border-neutral-800/60 p-3 space-y-2">
          <div className="text-xs text-neutral-300">Release Audios</div>
          <div className="space-y-2">
            {releaseRunRows.map((r) => (
              <div key={`release-run-${r.runId}`} className="flex items-center gap-3">
                <div className="w-28 shrink-0" title={`Run ${r.runId}`}>
                  <div className="text-[11px] font-mono text-neutral-300">v{r.version}</div>
                  <div className="text-[10px] text-neutral-600">{r.createdAt ? timeAgo(r.createdAt) : '—'}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <OutcomeTrackLineAudioPlayer gsUri={r.audioUri} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {nonReleaseRunIds.map((rid) => (
        <RunOutcomesBlock key={rid} runId={rid} idScope={id!} isOnlyRun={nonReleaseRunIds.length === 1 && releaseRunRows.length === 0} scopeNamespace={scopeNamespace} createdAt={runTimestamps[rid]} />
      ))}
    </div>
  );
}

/** Fetches + renders outcomes for a single run within a scope. */
function RunOutcomesBlock({
  runId,
  idScope: _idScope,
  isOnlyRun,
  scopeNamespace,
  createdAt,
}: {
  runId: number;
  idScope: number;
  isOnlyRun: boolean;
  scopeNamespace: string;
  createdAt?: string;
}) {
  const outcomesQuery = useQuery({
    queryKey: ['developer', 'workflowScopes', 'runOutcomes', runId] as const,
    queryFn: async () => {
      return await bff.get<{
        run_outcomes?: WorkflowRunOutcomeRow[];
        step_outcomes?: WorkflowRunOutcomeRow[];
      }>(`/orchestrator/runs/${encodeURIComponent(String(runId))}/outcomes`);
    },
    staleTime: 10_000,
  });

  const runRows: WorkflowRunOutcomeRow[] = useMemo(
    () => (outcomesQuery.data as any)?.run_outcomes ?? [],
    [outcomesQuery.data],
  );
  const stepRows: WorkflowRunOutcomeRow[] = useMemo(
    () => (outcomesQuery.data as any)?.step_outcomes ?? [],
    [outcomesQuery.data],
  );

  /* Separate run-level aggregate outcomes from per-step duration outcomes. */
  const runAggregateRows = useMemo(
    () => runRows.filter((r) => !r.HandlerKey || r.HandlerKey === '__run__'),
    [runRows],
  );

  const stepDurationRows = useMemo(
    () => runRows.filter((r) => r.HandlerKey && r.HandlerKey !== '__run__'),
    [runRows],
  );

  /* Group step rows by StepKey. */
  const stepGroups = useMemo(() => {
    const map = new Map<string, WorkflowRunOutcomeRow[]>();
    for (const row of stepRows) {
      const key = (row.StepKey ?? '').trim();
      if (!key) continue;
      const list = map.get(key) ?? [];
      list.push(row);
      map.set(key, list);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [stepRows]);

  /* Check for a scope-specific renderer. */
  const renderer = useMemo(() => lookupScopeRenderer(scopeNamespace), [scopeNamespace]);

  if (outcomesQuery.isLoading) return <div className="text-xs text-neutral-400">Loading run {runId}…</div>;
  if (outcomesQuery.error)
    return <div className="text-xs text-rose-400">Failed run {runId}: {(outcomesQuery.error as any)?.message ?? String(outcomesQuery.error)}</div>;
  if (runRows.length === 0 && stepRows.length === 0)
    return <div className="text-xs text-neutral-500">No outcomes for run {runId}.</div>;

  if (renderer) {
    const { Component } = renderer;
    return (
      <div className={isOnlyRun ? 'space-y-3' : 'space-y-3 border-l-2 border-neutral-800 pl-3'}>
        <div className="text-[10px] text-neutral-500">
          Run <span className="text-neutral-300">{runId}</span>
          {createdAt && <> · <span className="text-neutral-600" title={createdAt}>{timeAgo(createdAt)}</span></>}
          {' '}—{' '}
          Run outcomes: {runAggregateRows.length} · Step outcomes: {stepRows.length}
        </div>
        <Suspense fallback={<div className="text-xs text-neutral-400 py-2">Loading view…</div>}>
          <Component runId={runId} runRows={runRows} stepRows={stepRows} />
        </Suspense>
      </div>
    );
  }

  return (
    <div className={isOnlyRun ? 'space-y-3' : 'space-y-3 border-l-2 border-neutral-800 pl-3'}>
      <div className="text-[10px] text-neutral-500">
        Run <span className="text-neutral-300">{runId}</span>
        {createdAt && <> · <span className="text-neutral-600" title={createdAt}>{timeAgo(createdAt)}</span></>}
        {' '}—{' '}
        Run outcomes: {runAggregateRows.length} · Step durations: {stepDurationRows.length} · Step outcomes: {stepRows.length}
      </div>

      {/* Run aggregate outcomes (__run__ handler_key) */}
      {runAggregateRows.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs text-neutral-400">Run outcomes</div>
          <OutcomeTable rows={runAggregateRows} showStep={false} />
        </div>
      )}

      {/* Per-step duration badges (run_outcomes with handler_key != __run__) */}
      {stepDurationRows.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs text-neutral-400">Step durations</div>
          <div className="flex flex-wrap gap-2">
            {stepDurationRows.map((r, idx) => {
              const ms = typeof r.Value === 'number' ? r.Value : Number(r.Value ?? 0);
              const label = fmtDurationMs(ms);
              const hk = r.HandlerKey ?? '?';
              return (
                <div
                  key={`${hk}:${idx}`}
                  className="inline-flex items-center gap-1.5 rounded-md border border-neutral-800 px-2 py-1"
                  title={`${hk}: ${ms}ms`}
                >
                  <span className="text-[11px] text-neutral-400 font-mono truncate max-w-[200px]">{hk}</span>
                  <span className="text-[11px] text-neutral-200 font-mono font-medium">{label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Step outcomes grouped by step */}
      {stepGroups.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs text-neutral-400">Step outcomes</div>
          <div className="space-y-3">
            {stepGroups.map(([stepKey, rows]) => (
              <div key={stepKey} className="space-y-1">
                <div className="text-xs text-neutral-300">{stepKey}</div>
                <OutcomeTable rows={rows} showStep={false} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function WorkflowScopesPanel() {
  /* ── namespace data (lightweight) ─────────────────────────────── */
  const { data: namespacesRaw = [], isLoading: nsLoading, error: nsError } = useQuery<ScopeNamespaceCount[], Error>({
    queryKey: NS_QUERY_KEY,
    queryFn: async () => {
      const res = await fetchScopeNamespacesApi();
      return (res?.items ?? []) as ScopeNamespaceCount[];
    },
    staleTime: 30_000,
  });

  /* ── local state ──────────────────────────────────────────────── */
  const [selectedNs, setSelectedNs] = useState<string | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('created');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [nsSearch, setNsSearch] = useState('');
  const [keySearch, setKeySearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<WorkflowScope | null>(null);
  const [newScopeOpen, setNewScopeOpen] = useState(false);
  const [newScopeRunId, setNewScopeRunId] = useState<string | null>(null);

  /* ── workflow catalog (for "New Scope" runner form) ───────────── */
  const { data: workflows = [] } = useQuery<WorkflowCatalogEntry[], Error>({
    queryKey: ['developer', 'workflowRunner', 'workflowCatalog'] as const,
    queryFn: async () => {
      const res = await fetchWorkflowsV2Api({ limit: 500 });
      const items = (res.items ?? []) as unknown as WorkflowCatalogEntry[];
      return items.filter((w) => w?.is_active === true);
    },
    staleTime: 60_000,
  });

  /** Derive workflow_key from scope namespace (e.g. "workflow_run:music_production.create_project" → "music_production.create_project"). */
  const nsWorkflowKey = useMemo(() => {
    if (!selectedNs) return null;
    const m = selectedNs.match(/^workflow_run:(.+)$/);
    return m ? m[1] : null;
  }, [selectedNs]);

  /** Find the latest-version active workflow matching the namespace. */
  const nsWorkflow = useMemo(() => {
    if (!nsWorkflowKey) return null;
    const matches = workflows
      .filter((w) => w.workflow_key === nsWorkflowKey)
      .sort((a, b) => b.version - a.version);
    return matches[0] ?? null;
  }, [nsWorkflowKey, workflows]);

  /* ── filtered namespaces (client-side text filter) ────────────── */
  const namespaces = useMemo(() => {
    const term = nsSearch.trim().toLowerCase();
    if (!term) return namespacesRaw;
    return namespacesRaw.filter((n) => n.scope_namespace.toLowerCase().includes(term));
  }, [namespacesRaw, nsSearch]);

  /* ── per-namespace scope keys (server-filtered, on-demand) ───── */
  const {
    data: scopeKeysRaw = [],
    isLoading: keysLoading,
  } = useQuery<WorkflowScope[], Error>({
    queryKey: ['developer', 'scopeKeys', selectedNs] as const,
    queryFn: async () => {
      if (!selectedNs) return [];
      const res = await fetchWorkflowScopesApi({ scope_namespace: selectedNs, limit: SCOPE_KEY_LIMIT });
      return (res?.items ?? res ?? []) as WorkflowScope[];
    },
    enabled: !!selectedNs,
    staleTime: 30_000,
  });

  /* ── scope key display helper ─────────────────────────────────── */
  const scopeKeyDisplay = (s: WorkflowScope): string => {
    const raw = sk(s);
    const decoded = decodeScopeKey(raw);
    if (!decoded) return raw;
    const prefix = datePrefix(s.created_at);
    const slug = decoded.fields.map(([, v]) => sanitizeName(v)).join('_');
    return prefix ? `${prefix}_${slug}` : slug;
  };

  /* ── filtered + sorted scope keys ─────────────────────────────── */
  const filteredScopes = useMemo(() => {
    const term = keySearch.trim().toLowerCase();
    if (!term) return scopeKeysRaw;
    return scopeKeysRaw.filter((s) => {
      const display = scopeKeyDisplay(s).toLowerCase();
      const raw = sk(s).toLowerCase();
      const decoded = decodeScopeKey(sk(s));
      const label = decoded?.label?.toLowerCase() ?? '';
      return display.includes(term) || raw.includes(term) || label.includes(term);
    });
  }, [scopeKeysRaw, keySearch]);

  const sortedScopes = useMemo(() => {
    const dirMul = sortDir === 'asc' ? 1 : -1;

    const ts = (iso: string | undefined): number => {
      if (!iso) return 0;
      const t = new Date(iso).getTime();
      return Number.isFinite(t) ? t : 0;
    };

    const withIndex = filteredScopes.map((s, idx) => ({ s, idx }));
    withIndex.sort((a, b) => {
      const A = a.s;
      const B = b.s;

      let cmp = 0;
      if (sortKey === 'scopeKey') {
        cmp = scopeKeyDisplay(A).localeCompare(scopeKeyDisplay(B));
      } else if (sortKey === 'created') {
        cmp = ts(A.created_at) - ts(B.created_at);
      } else if (sortKey === 'updated') {
        cmp = ts(A.updated_at) - ts(B.updated_at);
      }

      if (cmp !== 0) return cmp * dirMul;
      return a.idx - b.idx;
    });

    return withIndex.map((x) => x.s);
  }, [filteredScopes, sortDir, sortKey]);

  /* ── render ───────────────────────────────────────────────────── */
  if (nsLoading) {
    return <div className="p-4 text-sm text-neutral-400">Loading scopes…</div>;
  }
  if (nsError) {
    return <div className="p-4 text-sm text-rose-400">Failed to load scopes: {nsError.message}</div>;
  }

  const rowKey = (s: WorkflowScope, i: number) => `${ns(s)}::${sk(s)}::${i}`;

  return (
    <div className="h-full min-h-0 grid grid-cols-[320px_1fr] gap-4">
      <NamespaceList
        namespaces={namespaces}
        selectedNamespace={selectedNs}
        onSelect={(ns) => {
          setSelectedNs(ns);
          setExpandedKey(null);
          setKeySearch('');
          setNewScopeOpen(false);
          setNewScopeRunId(null);
        }}
        searchValue={nsSearch}
        onSearchChange={setNsSearch}
        loading={false}
      />

      {/* ── Right: scope keys table ──────────────────────────────── */}
      <div className="rounded-md border border-neutral-800 flex flex-col min-h-0">
        <div className="px-3 py-2 border-b border-neutral-800 flex items-center justify-between">
          <span className="text-sm text-neutral-200">
            {selectedNs ? (
              <>
                Scope Keys —{' '}
                <span className="text-neutral-400">{selectedNs}</span>
              </>
            ) : (
              'Scope Keys'
            )}
          </span>
          {selectedNs && (
            <span className="text-xs text-neutral-500">
              {filteredScopes.length}{filteredScopes.length !== scopeKeysRaw.length ? ` / ${scopeKeysRaw.length}` : ''} keys
            </span>
          )}
        </div>

        {/* Scope key search (only when namespace selected) */}
        {selectedNs && (
          <div className="px-3 py-2 border-b border-neutral-800/50">
            <SearchInput value={keySearch} onChange={setKeySearch} placeholder="Filter scope keys…" />
          </div>
        )}

        {/* ── New Scope accordion ────────────────────────────────── */}
        {selectedNs && nsWorkflow && (
          <div className="border-b border-neutral-800/50">
            <button
              type="button"
              onClick={() => setNewScopeOpen((o) => !o)}
              className={
                'w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors cursor-default ' +
                (newScopeOpen
                  ? 'text-neutral-100 border-neutral-600'
                  : 'text-neutral-400 hover:text-neutral-200 hover:border-neutral-600')
              }
            >
              <ChevronDown
                className={
                  'w-3.5 h-3.5 shrink-0 transition-transform duration-150 ' +
                  (newScopeOpen ? 'rotate-0' : '-rotate-90')
                }
              />
              <span>New Scope</span>
            </button>

            <div
              className={
                'overflow-hidden transition-[max-height,opacity] duration-200 ease-out ' +
                (newScopeOpen ? 'max-h-[900px] opacity-100' : 'max-h-0 opacity-0 pointer-events-none')
              }
            >
              <div className="px-3 py-3 grid grid-cols-2 gap-4">
                {/* Left: runner form */}
                <div>
                  <WorkflowRunnerForm
                    workflowKey={nsWorkflow.workflow_key}
                    version={nsWorkflow.version}
                    definition={nsWorkflow.definition}
                    onRunStarted={(res) => setNewScopeRunId(res.workflow_run_id)}
                  />
                </div>

                {/* Right: live graph */}
                <div className="rounded-md border border-neutral-800 overflow-hidden min-h-[280px]">
                  <WorkflowRunGraphLive
                    definition={nsWorkflow.definition}
                    runId={newScopeRunId}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto p-3">
          {!selectedNs ? (
            <div className="h-full flex items-center justify-center">
              <span className="text-sm text-neutral-500">Select a namespace to view its keys.</span>
            </div>
          ) : keysLoading ? (
            <div className="h-full flex items-center justify-center">
              <span className="text-sm text-neutral-400">Loading keys…</span>
            </div>
          ) : filteredScopes.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <span className="text-sm text-neutral-500">
                {keySearch ? 'No matching keys.' : 'No keys for this namespace.'}
              </span>
            </div>
          ) : (
            <DarkDataTable<WorkflowScope>
              columns={[
                {
                  header: (
                    <SortHeader
                      label="Scope Key"
                      active={sortKey === 'scopeKey'}
                      dir={sortDir}
                      title="Sort by scope key"
                      onClick={() => {
                        if (sortKey === 'scopeKey') {
                          setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                        } else {
                          setSortKey('scopeKey');
                          setSortDir('asc');
                        }
                      }}
                    />
                  ),
                  tdClassName: 'text-neutral-200',
                  render: (s) => {
                    const raw = sk(s);
                    const decoded = decodeScopeKey(raw);
                    if (!decoded) {
                      return (
                        <div className="truncate max-w-[400px]" title={raw}>
                          {raw}
                        </div>
                      );
                    }
                    const prefix = datePrefix(s.created_at);
                    const slug = decoded.fields.map(([, v]) => sanitizeName(v)).join('_');
                    const display = prefix ? `${prefix}_${slug}` : slug;
                    return (
                      <div className="truncate max-w-[500px]" title={raw}>
                        <span className="font-mono text-neutral-100">{display}</span>
                        <span className="ml-2 text-[10px] text-neutral-500">
                          {decoded.label}
                        </span>
                      </div>
                    );
                  },
                },
                {
                  header: (
                    <SortHeader
                      label="Created"
                      active={sortKey === 'created'}
                      dir={sortDir}
                      title="Sort by created time"
                      onClick={() => {
                        if (sortKey === 'created') {
                          setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                        } else {
                          setSortKey('created');
                          setSortDir('desc');
                        }
                      }}
                    />
                  ),
                  thClassName: 'whitespace-nowrap',
                  tdClassName: 'text-neutral-500 whitespace-nowrap',
                  render: (s) => {
                    const ts = s.created_at;
                    if (!ts) return '—';
                    return <span title={ts}>{timeAgo(ts)}</span>;
                  },
                },
                {
                  header: (
                    <SortHeader
                      label="Updated"
                      active={sortKey === 'updated'}
                      dir={sortDir}
                      title="Sort by updated time"
                      onClick={() => {
                        if (sortKey === 'updated') {
                          setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                        } else {
                          setSortKey('updated');
                          setSortDir('desc');
                        }
                      }}
                    />
                  ),
                  thClassName: 'whitespace-nowrap',
                  tdClassName: 'text-neutral-500 whitespace-nowrap',
                  render: (s) => {
                    const ts = s.updated_at;
                    if (!ts) return '—';
                    return <span title={ts}>{timeAgo(ts)}</span>;
                  },
                },
                {
                  header: '',
                  thClassName: 'w-8',
                  tdClassName: 'w-8 text-center',
                  render: (s) => (
                    <button
                      type="button"
                      title="Delete scope and all associated data"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget(s);
                      }}
                      className="p-1 rounded text-neutral-600 hover:text-rose-400 hover:bg-rose-950/30 transition-colors cursor-default"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  ),
                },
              ]}
              rows={sortedScopes}
              getRowKey={rowKey}
              emptyMessage="No scope keys."
              frame={false}
              clickCursor="default"
              onRowClick={(s) => {
                const k = sk(s);
                setExpandedKey((prev) => (prev === k ? null : k));
              }}
              isRowSelected={(s) => sk(s) === expandedKey}
              isRowExpanded={(s) => sk(s) === expandedKey}
              renderExpandedRow={(s) =>
                selectedNs === 'device.instance' ? (
                  <Suspense fallback={<div className="px-3 py-3 text-xs text-neutral-400">Loading…</div>}>
                    <DeviceInstanceInline scopeKey={sk(s)} />
                  </Suspense>
                ) : (
                  <ScopeOutcomesInline scope={s} scopeNamespace={selectedNs ?? ''} />
                )
              }
            />
          )}
        </div>
      </div>

      {/* ── Scope delete confirmation modal ──────────────────────── */}
      <ScopeDeleteModal
        open={deleteTarget != null}
        onClose={() => setDeleteTarget(null)}
        scopeNamespace={selectedNs ?? ''}
        scopeKey={deleteTarget ? sk(deleteTarget) : ''}
        scopeLabel={deleteTarget ? scopeKeyDisplay(deleteTarget) : ''}
        idScope={deleteTarget ? idScope(deleteTarget) : undefined}
      />
    </div>
  );
}
